import { useCallback, useEffect, useRef, useState } from 'react'

// Wave 36 (Bar 3 trust scaffolding) — defer-and-undo pattern.
//
// Many destructive actions in the studio (archive a post, dismiss an idea,
// deprecate a pillar) can be handled with a soft delay rather than a hard
// commit. The operator sees an Undo toast for N seconds; if they click it
// the underlying API call never fires. If they don't, the action commits.
//
// This is NOT a soft-delete column. There is no DB write at all during the
// pending window — the request is queued in JS memory and flushed on
// timeout. That keeps the contract with the server identical: when the
// committed call lands, it is the destructive operation it always was.
//
// Why not just rely on a "restore" path? A restore path requires the user
// to know what to restore, navigate to the right tab, click the right
// thing. Undo is a single click on the toast that's already on screen.

export interface UndoableActionConfig<TArgs extends unknown[], TResult> {
  /**
   * The destructive operation to defer. Receives the same args passed to
   * `run`. Resolves with whatever the server returned. Rejection is
   * surfaced through `onError`.
   */
  commit: (...args: TArgs) => Promise<TResult>
  /**
   * Human-readable summary of what's about to happen, e.g.
   * "Post archived". Surfaced as the toast body.
   */
  describe: (...args: TArgs) => string
  /** Called once the toast appears. Lets callers update local UI state
   *  optimistically (e.g. hide the row immediately while undo is open). */
  onPending?: (...args: TArgs) => void
  /** Called if the operator clicks Undo within the window. Lets callers
   *  reverse any optimistic state change. */
  onUndo?: (...args: TArgs) => void
  /** Called when the deferred commit resolves. */
  onCommit?: (result: TResult, ...args: TArgs) => void
  /** Called when the deferred commit rejects. */
  onError?: (error: Error, ...args: TArgs) => void
  /**
   * Pending window in ms. Default 8 seconds — long enough for the
   * operator to react, short enough that they don't forget the toast is
   * still active. Plan-recommended value.
   */
  delayMs?: number
}

export interface UndoableActionState {
  /** True between toast-shown and either undo or commit. */
  pending: boolean
  /** The message currently displayed in the toast, or empty. */
  message: string
  /** Handler the toast button calls. No-op if no pending action. */
  undo: () => void
}

/**
 * @param config The undo behavior for this action.
 * @returns `[run, state]` — `run` triggers the deferred commit, `state` is
 *          the toast surface model.
 */
export function useUndoableAction<TArgs extends unknown[], TResult>(
  config: UndoableActionConfig<TArgs, TResult>
): [(...args: TArgs) => void, UndoableActionState] {
  const [state, setState] = useState<UndoableActionState>({
    pending: false,
    message: '',
    undo: () => {},
  })
  const pendingRef = useRef<{
    timer: ReturnType<typeof setTimeout> | null
    args: TArgs | null
    cancelled: boolean
  } | null>(null)

  // On unmount, fire any pending commit synchronously to avoid losing
  // operator intent. The browser tab close case still loses the action,
  // but this at least covers tab navigation within the SPA.
  useEffect(() => {
    return () => {
      const handle = pendingRef.current
      if (!handle || handle.cancelled || !handle.args) return
      if (handle.timer) clearTimeout(handle.timer)
      // Best-effort flush — ignore the promise; the hook is gone.
      void config.commit(...handle.args).catch((err) => {
        if (config.onError)
          config.onError(
            err instanceof Error ? err : new Error(String(err)),
            ...handle.args!
          )
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const reset = useCallback(() => {
    pendingRef.current = null
    setState({ pending: false, message: '', undo: () => {} })
  }, [])

  const run = useCallback(
    (...args: TArgs) => {
      const delay = Math.max(1000, Math.min(60_000, config.delayMs ?? 8000))
      // If a previous undo is still pending, commit it first so we don't
      // lose the operator's earlier intent.
      const previous = pendingRef.current
      if (previous && previous.args && !previous.cancelled) {
        if (previous.timer) clearTimeout(previous.timer)
        const previousArgs = previous.args
        void config
          .commit(...previousArgs)
          .then((result) => config.onCommit?.(result, ...previousArgs))
          .catch((err) =>
            config.onError?.(
              err instanceof Error ? err : new Error(String(err)),
              ...previousArgs
            )
          )
      }

      const handle: {
        timer: ReturnType<typeof setTimeout> | null
        args: TArgs
        cancelled: boolean
      } = {
        timer: null,
        args,
        cancelled: false,
      }
      pendingRef.current = handle

      handle.timer = setTimeout(() => {
        if (handle.cancelled) return
        config
          .commit(...args)
          .then((result) => {
            if (handle.cancelled) return
            config.onCommit?.(result, ...args)
          })
          .catch((err) => {
            if (handle.cancelled) return
            config.onError?.(
              err instanceof Error ? err : new Error(String(err)),
              ...args
            )
          })
          .finally(() => {
            if (pendingRef.current === handle) reset()
          })
      }, delay)

      const undo = () => {
        if (handle.cancelled) return
        handle.cancelled = true
        if (handle.timer) clearTimeout(handle.timer)
        config.onUndo?.(...args)
        if (pendingRef.current === handle) reset()
      }

      config.onPending?.(...args)
      setState({
        pending: true,
        message: config.describe(...args),
        undo,
      })
    },
    [config, reset]
  )

  return [run, state]
}
